/*
  Simple Drawer 26 — inline accordion behaviour for the navigation drawer.

  The drawer's markup is a plain nested list. Each expandable row has an anchor
  and, separately, a <button aria-expanded aria-controls>. This element only
  owns the buttons: anchors are left completely alone, so clicking an icon or a
  label always navigates.

  Collapsed lists carry the `hidden` attribute, which keeps their contents out
  of the tab order without any extra bookkeeping.

  Everything else about the drawer — opening, the overlay, scroll locking,
  Escape, swipe to close — still belongs to the theme's own sidebar component.
*/
(function () {
  'use strict';

  var TOGGLE_SELECTOR = '.drawer-nav__toggle';
  var OPENER_ID = 'DrawerMenuToggler';

  if (customElements.get('drawer-nav')) return;

  class DrawerNav extends HTMLElement {
    constructor() {
      super();
      this.hadFocus = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      // Registered on the element itself, so they go away with the element and
      // cannot be added twice when the drawer is opened again or when the
      // theme editor reloads the header section.
      this.addEventListener('click', this.handleClick);
      this.addEventListener('focusin', this.handleFocusIn);
    }

    connectedCallback() {
      // The sidebar re-inserts this subtree every time the drawer opens.
      // Always start at level 0 with every branch closed.
      this.collapseAll();
      this.hadFocus = false;
    }

    disconnectedCallback() {
      // The sidebar empties itself on close, whichever way it was closed:
      // Escape, the overlay, a swipe or the close button. Removing the focused
      // element drops focus on <body>, so hand it back to the opener.
      if (!this.hadFocus) return;
      if (window.Shopify && window.Shopify.designMode) return;
      if (document.activeElement && document.activeElement !== document.body) return;

      var opener = document.getElementById(OPENER_ID);
      if (opener) opener.focus();
    }

    handleFocusIn() {
      this.hadFocus = true;
    }

    handleClick(event) {
      var toggle = event.target.closest(TOGGLE_SELECTOR);

      if (!toggle || !this.contains(toggle)) return;

      this.setExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
    }

    setExpanded(toggle, isExpanded) {
      var panel = this.panelFor(toggle);

      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

      if (!panel) return;

      panel.hidden = !isExpanded;

      // Closing a branch closes what was open inside it, so reopening it later
      // shows the same thing it showed the first time.
      if (!isExpanded) this.collapseAll(panel);
    }

    panelFor(toggle) {
      var id = toggle.getAttribute('aria-controls');
      return id ? this.querySelector('#' + CSS.escape(id)) : null;
    }

    collapseAll(root) {
      var self = this;

      (root || this).querySelectorAll(TOGGLE_SELECTOR).forEach(function (toggle) {
        var panel = self.panelFor(toggle);

        toggle.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }
  }

  customElements.define('drawer-nav', DrawerNav);
})();
